import React from 'react';

export interface ButtonProps {
  /** Button style variant */
  variant?: 'primary' | 'secondary' | 'tertiary' | 'warning' | 'success';
  /** Button size */
  size?: 'small' | 'medium' | 'large';
  /** Disabled state */
  disabled?: boolean;
  /** Loading state - shows loading indicator and disables button */
  loading?: boolean;
  /** Full width button */
  fullWidth?: boolean;
  /** Click handler */
  onClick?: (
    event: React.MouseEvent<HTMLButtonElement | HTMLAnchorElement | HTMLInputElement>
  ) => void;
  /** Mouse down handler */
  onMouseDown?: (
    event: React.MouseEvent<HTMLButtonElement | HTMLAnchorElement | HTMLInputElement>
  ) => void;
  /** Button type (defaults to 'button') */
  type?: 'button' | 'submit' | 'reset';
  /** Button content */
  children: React.ReactNode;
  /**
   * Override the rendered HTML element.
   * - 'button' (default): Renders <button> element
   * - 'a': Renders <a> element (use with href prop)
   * - 'input': Renders <input> element (uses children as value)
   */
  as?: 'button' | 'a' | 'input';
  /** Custom CSS classes */
  className?: string;
  /** Inline styles */
  style?: React.CSSProperties;
  /** ARIA label */
  'aria-label'?: string;
  /** ARIA described by */
  'aria-describedby'?: string;
  /** ARIA controls */
  'aria-controls'?: string;
  /** ARIA expanded */
  'aria-expanded'?: boolean;
  /** ARIA has popup */
  'aria-haspopup'?: boolean | 'true' | 'false' | 'menu' | 'listbox' | 'tree' | 'grid' | 'dialog';
  /** Form button name (for submit buttons in forms) */
  name?: string;
  /** Form button value (for submit buttons in forms) */
  value?: string;
  /** Link href (renders as anchor when provided) */
  href?: string;
  /** Link target */
  target?: '_blank' | '_self' | '_parent' | '_top';
  /** Link rel attribute */
  rel?: string;
}

export const Button: React.FC<ButtonProps> = ({
  variant = 'primary',
  size = 'medium',
  disabled = false,
  loading = false,
  fullWidth = false,
  onClick,
  onMouseDown,
  type = 'button',
  children,
  as,
  className = '',
  style,
  'aria-label': ariaLabel,
  'aria-describedby': ariaDescribedby,
  'aria-controls': ariaControls,
  'aria-expanded': ariaExpanded,
  'aria-haspopup': ariaHaspopup,
  name,
  value,
  href,
  target,
  rel,
}) => {
  // Determine element type
  // If 'as' is explicitly set, use it
  // Otherwise, if href is provided, render as link
  // Otherwise, render as button
  const elementType = as || (href ? 'a' : 'button');
  const isLink = elementType === 'a';
  const isInput = elementType === 'input';

  // Base classes (always applied) - Heart Internet brand styles
  // Add 'hi-ui' wrapper class for CSS isolation
  const baseClasses = 'hi-ui tw-btn-base';

  // Variant classes (use CSS variables via custom classes)
  const variantClasses = {
    primary: 'tw-btn-primary',
    secondary: 'tw-btn-secondary',
    tertiary: 'tw-btn-tertiary',
    warning: 'tw-btn-warning',
    success: 'tw-btn-success',
  };

  // Size classes - can scale the base sizing if needed
  const sizeClasses = {
    small: 'tw-btn-size-small',
    medium: 'tw-btn-size-medium',
    large: 'tw-btn-size-large',
  };

  // Width class
  const widthClass = fullWidth ? 'tw-w-full' : 'tw-w-auto';

  // Disabled/loading state is handled in CSS now
  const stateClass = disabled || loading ? 'tw-btn-disabled' : '';

  const allClasses =
    `${baseClasses} ${variantClasses[variant]} ${sizeClasses[size]} ${widthClass} ${stateClass} ${className}`.trim();

  // Common props for both button and anchor
  const commonProps = {
    className: allClasses,
    style,
    onClick,
    onMouseDown,
    'aria-label': ariaLabel,
    'aria-describedby': ariaDescribedby,
    'aria-controls': ariaControls,
    'aria-expanded': ariaExpanded,
    'aria-haspopup': ariaHaspopup,
  };

  // Render as input if as='input'
  if (isInput) {
    return (
      <input
        {...commonProps}
        type={type}
        name={name}
        value={value ?? (typeof children === 'string' ? children : undefined)}
        disabled={disabled || loading}
        aria-busy={loading}
        aria-disabled={disabled || loading}
      />
    );
  }

  // Render as link if as='a' or href is provided
  if (isLink) {
    return (
      <a
        {...commonProps}
        href={disabled || loading ? undefined : href}
        target={target}
        rel={target === '_blank' ? rel || 'noopener noreferrer' : rel}
        aria-disabled={disabled || loading}
        role="button"
        tabIndex={disabled || loading ? -1 : undefined}
      >
        {loading ? 'Loading...' : children}
      </a>
    );
  }

  // Render as button (default)
  return (
    <button
      {...commonProps}
      type={type}
      name={name}
      value={value}
      disabled={disabled || loading}
      aria-busy={loading}
      aria-disabled={disabled || loading}
    >
      {loading ? 'Loading...' : children}
    </button>
  );
};
